import {isLoaded, PackageModel, UnassignedPackageModel} from 'ApplicationEntry/models/ProductsRiders/PackageModel';
import {Loaded} from 'Core/models/Async';
import {EmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

type AIPProps = {
  units: StringUnion;
  planType: StringUnion;
  indWeeklyPremium: StringUnion;
  familyWeeklyPremium: StringUnion;
  indMonthlyPremium: StringUnion;
  familyMonthlyPremium: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  section125: boolean;
  packageOption: StringUnion;
  situs: StringUnion;
  jstamp: boolean;
  packageList: PackageModel;
};

export class InvalidNewYorkAIP {
  public readonly key = 'InvalidNewYorkAIP';

  constructor(
    public readonly units: StringUnion,
    public readonly planType: StringUnion,
    public readonly indWeeklyPremium: StringUnion,
    public readonly familyWeeklyPremium: StringUnion,
    public readonly indMonthlyPremium: StringUnion,
    public readonly familyMonthlyPremium: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly section125: boolean,
    public readonly packageOption: StringUnion,
    public readonly situs: StringUnion,
    public readonly jstamp: boolean,
    public readonly packageList: PackageModel
  ) {}
}

export class ValidNewYorkAIP {
  public static createApp({
    units,
    planType,
    indWeeklyPremium,
    familyWeeklyPremium,
    indMonthlyPremium,
    familyMonthlyPremium,
    premium,
    calculatedPremium,
    cwa,
    section125,
    packageOption,
    situs,
    jstamp,
    packageList,
  }: AIPProps) {
    return this.create(
      units,
      planType,
      indWeeklyPremium,
      familyWeeklyPremium,
      indMonthlyPremium,
      familyMonthlyPremium,
      premium,
      calculatedPremium,
      cwa,
      section125,
      packageOption,
      situs,
      jstamp,
      packageList
    );
  }

  public static create(
    units: StringUnion,
    planType: StringUnion,
    indWeeklyPremium: StringUnion,
    familyWeeklyPremium: StringUnion,
    indMonthlyPremium: StringUnion,
    familyMonthlyPremium: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    section125: boolean,
    packageOption: StringUnion,
    situs: StringUnion,
    jstamp: boolean,
    packageList: PackageModel
  ) {
    return isLoaded(packageList)
      ? new ValidNewYorkAIP(
          units,
          planType,
          indWeeklyPremium,
          familyWeeklyPremium,
          indMonthlyPremium,
          familyMonthlyPremium,
          premium,
          calculatedPremium,
          cwa,
          section125,
          packageOption,
          situs,
          jstamp,
          packageList
        )
      : new InvalidNewYorkAIP(
          units,
          planType,
          indWeeklyPremium,
          familyWeeklyPremium,
          indMonthlyPremium,
          familyMonthlyPremium,
          premium,
          calculatedPremium,
          cwa,
          section125,
          packageOption,
          situs,
          jstamp,
          packageList
        );
  }

  public readonly key = 'ValidNewYorkAIP';
  constructor(
    public readonly units: StringUnion,
    public readonly planType: StringUnion,
    public readonly indWeeklyPremium: StringUnion,
    public readonly familyWeeklyPremium: StringUnion,
    public readonly indMonthlyPremium: StringUnion,
    public readonly familyMonthlyPremium: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly section125: boolean,
    public readonly packageOption: StringUnion,
    public readonly situs: StringUnion,
    public readonly jstamp: boolean,
    public readonly packageList: Loaded<object[]>
  ) {}
}

export type NewYorkAIP = InvalidNewYorkAIP | ValidNewYorkAIP;

export type GroupAIPOptions<TReturn> = ToPartialMatchOptions<NewYorkAIP, 'key', TReturn>;

export const matchNewYorkAIP = <TReturn>(options: GroupAIPOptions<TReturn>) => (app: NewYorkAIP): TReturn => {
  switch (app.key) {
    case 'InvalidNewYorkAIP':
      return options.InvalidNewYorkAIP ? options.InvalidNewYorkAIP(app) : options.default(app);
    case 'ValidNewYorkAIP':
      return options.ValidNewYorkAIP ? options.ValidNewYorkAIP(app) : options.default(app);
  }
};

export const EmptyNewYorkAIP = new InvalidNewYorkAIP(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  new EmptyString(),
  new EmptyString(),
  false,
  new UnassignedPackageModel()
);

export const changeUnits = (units: string) => (app: NewYorkAIP) =>
  ValidNewYorkAIP.createApp({...app, units: NonEmptyString.create(units)});
export const changePlanType = (planType: string) => (app: NewYorkAIP) =>
  ValidNewYorkAIP.createApp({...app, planType: NonEmptyString.create(planType)});
export const changeIndWeeklyPremium = (indWeeklyPremium: string) => (app: NewYorkAIP) =>
  ValidNewYorkAIP.createApp({...app, indWeeklyPremium: NonEmptyString.create(indWeeklyPremium)});
export const changeFamilyWeeklyPremium = (familyWeeklyPremium: string) => (app: NewYorkAIP) =>
  ValidNewYorkAIP.createApp({...app, familyWeeklyPremium: NonEmptyString.create(familyWeeklyPremium)});
export const changeIndMonthlyPremium = (indMonthlyPremium: string) => (app: NewYorkAIP) =>
  ValidNewYorkAIP.createApp({...app, indMonthlyPremium: NonEmptyString.create(indMonthlyPremium)});
export const changeFamilyMonthlyPremium = (familyMonthlyPremium: string) => (app: NewYorkAIP) =>
  ValidNewYorkAIP.createApp({...app, familyMonthlyPremium: NonEmptyString.create(familyMonthlyPremium)});
export const changePremium = (premium: string) => (app: NewYorkAIP) =>
  ValidNewYorkAIP.createApp({...app, premium: NonEmptyString.create(premium)});
export const changeCalculatedPremium = (calculatedPremium: string) => (app: NewYorkAIP) =>
  ValidNewYorkAIP.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});
export const changeCWA = (cwa: string) => (app: NewYorkAIP) =>
  ValidNewYorkAIP.createApp({...app, cwa: NonEmptyString.create(cwa)});
export const changeSection = (section: boolean) => (app: NewYorkAIP) =>
  ValidNewYorkAIP.createApp({...app, section125: section});
export const changePackage = (packageOption: string) => (app: NewYorkAIP) =>
  ValidNewYorkAIP.createApp({...app, packageOption: NonEmptyString.create(packageOption)});
export const changeSitus = (situs: string) => (app: NewYorkAIP) =>
  ValidNewYorkAIP.createApp({...app, situs: NonEmptyString.create(situs)});
export const changeJStamp = (jstamp: boolean) => (app: NewYorkAIP) => ValidNewYorkAIP.createApp({...app, jstamp});
export const changePackageList = (packages: PackageModel) => (app: NewYorkAIP) =>
  ValidNewYorkAIP.createApp({...app, packageList: packages});
